import React from "react";
import { MyRegistrationItem } from "../../api/getMyRegistrations/types";

interface MyRegistrationCardProps {
  registration: MyRegistrationItem;
}

function formatDateTime(
  startDate: string | null,
  endDate: string | null,
  timezoneShortName: string | null,
  timezoneIdentifier: string | null,
): string {
  if (!startDate) return "Date TBD";

  const tz = timezoneIdentifier ?? undefined;
  const start = new Date(startDate);
  const dateStr = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  });
  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });

  let result = `${dateStr} · ${startTime}`;

  if (endDate) {
    const end = new Date(endDate);
    const endTime = end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    result += ` - ${endTime}`;
  }

  if (timezoneShortName) {
    result += ` ${timezoneShortName}`;
  }

  return result;
}

function formatVenue(
  venueName: string | null,
  venueCity: string | null,
  venueState: string | null,
): string | null {
  if (!venueName && !venueCity) return null;

  const parts: string[] = [];
  if (venueName) parts.push(venueName);

  const location = [venueCity, venueState].filter(Boolean).join(", ");
  if (location) parts.push(location);

  return parts.join(" · ");
}

const MyRegistrationCard: React.FC<MyRegistrationCardProps> = ({
  registration,
}) => {
  const {
    eventName,
    sessionName,
    startDate,
    endDate,
    timezoneShortName,
    timezoneIdentifier,
    venueName,
    venueCity,
    venueState,
    relationship,
    confirmationNumber,
    isCancelled,
    attendees,
  } = registration;

  const dateTime = formatDateTime(
    startDate,
    endDate,
    timezoneShortName,
    timezoneIdentifier,
  );
  const venue = formatVenue(venueName, venueCity, venueState);

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {eventName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {sessionName}
          </p>
        </div>
        {isCancelled && (
          <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
            Cancelled
          </span>
        )}
      </div>

      <div className="mt-3 space-y-0.5">
        <p className="text-sm text-gray-500 dark:text-gray-400">{dateTime}</p>
        {venue && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{venue}</p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50">
        {(relationship === "enrolled" || relationship === "both") && (
          <p className="text-sm text-gray-900 dark:text-white">
            You're registered
            {confirmationNumber && (
              <span className="text-gray-400 dark:text-gray-500">
                {" "}
                · Conf: {confirmationNumber}
              </span>
            )}
          </p>
        )}

        {(relationship === "registered_others" || relationship === "both") &&
          attendees.length > 0 && (
            <div
              className={
                relationship === "both" ? "mt-1.5" : ""
              }
            >
              <p className="text-sm text-gray-900 dark:text-white">
                You registered {attendees.length}{" "}
                {attendees.length === 1 ? "person" : "people"}
                {relationship === "registered_others" &&
                  confirmationNumber && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {" "}
                      · Conf: {confirmationNumber}
                    </span>
                  )}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
                {attendees
                  .map((a) => `${a.firstName} ${a.lastName}`)
                  .join(", ")}
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

export default MyRegistrationCard;
